import React, { useState, useCallback, useEffect, useRef } from 'react'
import ReactFlow, {
  ReactFlowProvider,
  addEdge,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  Handle,
  Position,
  MarkerType,
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  useReactFlow,
} from 'reactflow'
import 'reactflow/dist/style.css'
import { useNavigate, useParams } from 'react-router-dom'
import api from '../../services/api'
import toast from 'react-hot-toast'

// ── Tema hacker (terminal verde) ──────────────────────────────────────────────
const MONO = "'JetBrains Mono', 'Fira Code', Consolas, 'Courier New', monospace"

const inputStyle = {
  width: '100%', padding: '8px 10px', borderRadius: 4,
  border: '1.5px solid #1a5c2e', background: '#000f03',
  color: '#39ff6a', fontSize: 12, boxSizing: 'border-box', outline: 'none',
  fontFamily: MONO,
}

// Límites razonables por unidad para el nodo "Esperar"
const LIMITES_UNIDAD = { segundos: 59, minutos: 59, horas: 23, dias: 30 }
const clampCantidad = (unidad, valor) => Math.max(1, Math.min(LIMITES_UNIDAD[unidad] || 59, parseInt(valor, 10) || 1))

function nodeCard(borderColor, selected) {
  return {
    border: `2px solid ${selected ? '#00ff41' : borderColor}`,
    borderRadius: 6,
    background: '#03170a',
    color: '#39ff6a',
    minWidth: 210,
    maxWidth: 240,
    boxShadow: selected
      ? '0 0 0 3px rgba(0,255,65,0.35), 0 0 18px rgba(0,255,65,0.45)'
      : `0 0 8px ${borderColor}33, 0 2px 8px rgba(0,0,0,0.6)`,
    fontFamily: MONO,
    fontSize: 12,
    cursor: 'pointer',
  }
}

function NodeHeader({ color, icon, label }) {
  return (
    <div style={{ background: color, borderRadius: '4px 4px 0 0', padding: '6px 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ fontSize: 14, filter: 'grayscale(0.3)' }}>{icon}</span>
      <span style={{ fontWeight: 700, fontSize: 11, color: '#000', letterSpacing: 0.5, textTransform: 'uppercase', fontFamily: MONO }}>{label}</span>
    </div>
  )
}

// ── Nodos personalizados ───────────────────────────────────────────────────────
function InicioNode({ data, selected }) {
  const kws = data.config?.palabrasClave || []
  const modo = data.config?.modoCoincidencia === 'exacto' ? 'Exacto' : 'Contiene'
  return (
    <div style={nodeCard('#00ff41', selected)}>
      <NodeHeader color="#00ff41" icon="🚀" label="Inicio · Palabras clave" />
      <div style={{ padding: '8px 10px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 5 }}>
          {kws.length ? kws.map((k, i) => (
            <span key={i} style={{ background: '#003d0f', color: '#7fffaa', borderRadius: 4, padding: '1px 6px', fontSize: 10 }}>{k}</span>
          )) : <span style={{ color: '#3d7a4a', fontSize: 11 }}>Sin palabras clave — editar</span>}
        </div>
        <span style={{ color: '#3d7a4a', fontSize: 10 }}>Modo: {modo}</span>
      </div>
      <Handle type="source" position={Position.Bottom} style={{ background: '#00ff41', width: 10, height: 10 }} />
    </div>
  )
}

function TextoNode({ data, selected }) {
  const txt = data.config?.texto || ''
  return (
    <div style={nodeCard('#00e676', selected)}>
      <NodeHeader color="#00994d" icon="📝" label="Texto" />
      <div style={{ padding: '8px 10px' }}>
        <p style={{ margin: 0, color: txt ? '#39ff6a' : '#3d7a4a', fontSize: 11, lineHeight: 1.5, maxHeight: 54, overflow: 'hidden' }}>
          {txt ? (txt.length > 90 ? txt.slice(0, 90) + '…' : txt) : 'Sin texto — clic para editar'}
        </p>
      </div>
      <Handle type="target" position={Position.Top} style={{ background: '#00e676', width: 10, height: 10 }} />
      <Handle type="source" position={Position.Bottom} style={{ background: '#00e676', width: 10, height: 10 }} />
    </div>
  )
}

function ImagenNode({ data, selected }) {
  const url = data.config?.mediaUrl
  const backendUrl = process.env.REACT_APP_BACKEND_URL || ''
  const previewUrl = url ? (url.startsWith('http') ? url : `${backendUrl}${url}`) : null
  return (
    <div style={nodeCard('#00e676', selected)}>
      <NodeHeader color="#00994d" icon="🖼" label="Imagen" />
      <div style={{ padding: '8px 10px' }}>
        {previewUrl ? (
          <img src={previewUrl} alt="preview" style={{ width: '100%', height: 72, objectFit: 'cover', borderRadius: 6 }} />
        ) : (
          <div style={{ height: 56, background: '#020f04', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#3d7a4a', fontSize: 11 }}>
            Sin imagen — clic para subir
          </div>
        )}
        {data.config?.caption && <p style={{ margin: '4px 0 0', color: '#3d7a4a', fontSize: 10 }}>{data.config.caption}</p>}
      </div>
      <Handle type="target" position={Position.Top} style={{ background: '#00e676', width: 10, height: 10 }} />
      <Handle type="source" position={Position.Bottom} style={{ background: '#00e676', width: 10, height: 10 }} />
    </div>
  )
}

function DocumentoNode({ data, selected }) {
  return (
    <div style={nodeCard('#00e676', selected)}>
      <NodeHeader color="#00994d" icon="📄" label="Documento" />
      <div style={{ padding: '8px 10px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 22 }}>📎</span>
        <span style={{ color: data.config?.mediaUrl ? '#39ff6a' : '#3d7a4a', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {data.config?.nombre || 'Sin documento — clic para subir'}
        </span>
      </div>
      <Handle type="target" position={Position.Top} style={{ background: '#00e676', width: 10, height: 10 }} />
      <Handle type="source" position={Position.Bottom} style={{ background: '#00e676', width: 10, height: 10 }} />
    </div>
  )
}

function VideoNode({ data, selected }) {
  const url = data.config?.mediaUrl
  const backendUrl = process.env.REACT_APP_BACKEND_URL || ''
  const previewUrl = url ? (url.startsWith('http') ? url : `${backendUrl}${url}`) : null
  return (
    <div style={nodeCard('#00e676', selected)}>
      <NodeHeader color="#00994d" icon="🎬" label="Video" />
      <div style={{ padding: '8px 10px' }}>
        {previewUrl ? (
          <video src={previewUrl} style={{ width: '100%', height: 72, objectFit: 'cover', borderRadius: 6 }} muted />
        ) : (
          <div style={{ height: 56, background: '#020f04', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#3d7a4a', fontSize: 11 }}>
            Sin video — clic para subir
          </div>
        )}
        {data.config?.caption && <p style={{ margin: '4px 0 0', color: '#3d7a4a', fontSize: 10 }}>{data.config.caption}</p>}
      </div>
      <Handle type="target" position={Position.Top} style={{ background: '#00e676', width: 10, height: 10 }} />
      <Handle type="source" position={Position.Bottom} style={{ background: '#00e676', width: 10, height: 10 }} />
    </div>
  )
}

function AudioNode({ data, selected }) {
  return (
    <div style={nodeCard('#00e676', selected)}>
      <NodeHeader color="#00994d" icon="🎵" label="Audio" />
      <div style={{ padding: '8px 10px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 22 }}>🎙</span>
        <span style={{ color: data.config?.mediaUrl ? '#39ff6a' : '#3d7a4a', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {data.config?.nombre || 'Sin audio — clic para subir'}
        </span>
      </div>
      <Handle type="target" position={Position.Top} style={{ background: '#00e676', width: 10, height: 10 }} />
      <Handle type="source" position={Position.Bottom} style={{ background: '#00e676', width: 10, height: 10 }} />
    </div>
  )
}

// Conexión con botón "×" para desvincular dos nodos sin borrarlos
function ConexionEliminable({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, style, markerEnd }) {
  const { setEdges } = useReactFlow()
  const [edgePath, labelX, labelY] = getBezierPath({ sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition })
  return (
    <>
      <BaseEdge id={id} path={edgePath} style={style} markerEnd={markerEnd} />
      <EdgeLabelRenderer>
        <button
          onClick={() => setEdges(es => es.filter(e => e.id !== id))}
          title="Desconectar (no elimina los nodos)"
          style={{
            position: 'absolute', transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            pointerEvents: 'all', width: 20, height: 20, borderRadius: '50%',
            border: '1.5px solid #ff4444', background: '#000f03', color: '#ff4444',
            fontSize: 12, fontWeight: 700, lineHeight: 1, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
          ×
        </button>
      </EdgeLabelRenderer>
    </>
  )
}
const EDGE_TYPES = { eliminable: ConexionEliminable }

const UNIDAD_ABREV = { segundos: 's', minutos: 'min', horas: 'h', dias: 'd' }

function EsperarNode({ data, selected }) {
  const cfg = data.config || {}
  const etiqueta = cfg.unidad
    ? `${cfg.cantidad ?? 1}${UNIDAD_ABREV[cfg.unidad] || cfg.unidad}`
    : `${cfg.segundos || 2}s` // formato legado
  return (
    <div style={nodeCard('#3d6b45', selected)}>
      <NodeHeader color="#1f4d2a" icon="⏱" label="Esperar" />
      <div style={{ padding: '10px', textAlign: 'center' }}>
        <span style={{ fontSize: 26, fontWeight: 700, color: '#5fae6f' }}>{etiqueta}</span>
        <p style={{ margin: '2px 0 0', color: '#3d7a4a', fontSize: 10 }}>Pausa antes del siguiente nodo</p>
      </div>
      <Handle type="target" position={Position.Top} style={{ background: '#3d7a4a', width: 10, height: 10 }} />
      <Handle type="source" position={Position.Bottom} style={{ background: '#3d7a4a', width: 10, height: 10 }} />
    </div>
  )
}

const NODE_TYPES = { inicio: InicioNode, texto: TextoNode, imagen: ImagenNode, documento: DocumentoNode, audio: AudioNode, video: VideoNode, esperar: EsperarNode }

const PALETA = [
  { tipo: 'inicio', icon: '🚀', label: 'Inicio / Trigger', color: '#00ff41', desc: 'Define palabras clave' },
  { tipo: 'texto',  icon: '📝', label: 'Texto',            color: '#00994d', desc: 'Envía un mensaje de texto' },
  { tipo: 'imagen', icon: '🖼', label: 'Imagen',           color: '#00994d', desc: 'jpg, png, webp' },
  { tipo: 'documento', icon: '📄', label: 'Documento',     color: '#00994d', desc: 'PDF, Word, Excel, ZIP' },
  { tipo: 'audio',  icon: '🎵', label: 'Audio',            color: '#00994d', desc: 'mp3, ogg' },
  { tipo: 'video',  icon: '🎬', label: 'Video',            color: '#00994d', desc: 'mp4, mov' },
  { tipo: 'esperar',icon: '⏱', label: 'Esperar',          color: '#1f4d2a', desc: 'Pausa entre mensajes' },
]

// ── Conversión DB ↔ ReactFlow ─────────────────────────────────────────────────
function dbToFlow(nodos, conexiones) {
  const rfNodes = (nodos || []).map(n => ({
    id: n.id, type: n.tipo, position: n.position || { x: 0, y: 0 },
    data: { config: n.config || {} },
  }))
  const rfEdges = (conexiones || []).map(c => ({
    id: c.id || `e-${c.desde}-${c.hacia}`,
    source: c.desde, target: c.hacia,
    type: 'eliminable',
    markerEnd: { type: MarkerType.ArrowClosed, color: '#00ff41' },
    style: { stroke: '#00ff41', strokeWidth: 2 },
  }))
  return { rfNodes, rfEdges }
}

function flowToDb(rfNodes, rfEdges) {
  return {
    nodos: rfNodes.map(n => ({ id: n.id, tipo: n.type, position: n.position, config: n.data.config || {} })),
    conexiones: rfEdges.map(e => ({ id: e.id, desde: e.source, hacia: e.target })),
  }
}

function genId() { return 'n' + Math.random().toString(36).slice(2, 8) }

// ── Panel de propiedades ───────────────────────────────────────────────────────
function PanelProps({ node, flujoId, onChange, onDelete, onDuplicate, mobile, onClose }) {
  const [cfg, setCfg] = useState(node.data.config || {})
  // Texto crudo de "Palabras clave" mientras se escribe — separado de cfg para
  // no recortar (trim) los espacios en cada tecla, lo que impedía escribir
  // claves de varias palabras (ej. "buenos dias"). Se convierte al arreglo
  // final recien en onBlur/save.
  const [kwText, setKwText] = useState((node.data.config?.palabrasClave || []).join(', '))
  const [subiendo, setSubiendo] = useState(false)
  const fileRef = useRef()
  const tipo = node.type

  useEffect(() => {
    setCfg(node.data.config || {})
    setKwText((node.data.config?.palabrasClave || []).join(', '))
  }, [node.id])

  const parsearPalabrasClave = (texto) => texto.split(',').map(s => s.trim()).filter(Boolean)

  const save = () => {
    let cfgFinal = tipo === 'esperar'
      ? { ...cfg, unidad: cfg.unidad || 'segundos', cantidad: clampCantidad(cfg.unidad || 'segundos', cfg.cantidad) }
      : cfg
    if (tipo === 'inicio') cfgFinal = { ...cfgFinal, palabrasClave: parsearPalabrasClave(kwText) }
    onChange(node.id, cfgFinal)
    if (mobile && onClose) onClose()
  }

  const subirMedia = async (file) => {
    setSubiendo(true)
    try {
      const form = new FormData()
      form.append('file', file)
      const res = await api.post(`/bot/flujos/${flujoId}/upload`, form, { headers: { 'Content-Type': 'multipart/form-data' } })
      const next = { ...cfg, mediaUrl: res.data.url, mimetype: res.data.mimetype, nombre: res.data.nombre }
      setCfg(next)
      onChange(node.id, next)
      toast.success('Archivo subido')
    } catch { toast.error('Error subiendo archivo') }
    finally { setSubiendo(false) }
  }

  const acceptFor = { imagen: 'image/*', audio: 'audio/*', video: 'video/*', documento: '.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip' }

  const rootStyle = mobile
    ? { width: '100%', background: '#020f04', display: 'flex', flexDirection: 'column', overflow: 'hidden', maxHeight: '75vh' }
    : { width: 272, background: '#020f04', borderLeft: '1px solid #0f3d1f', display: 'flex', flexDirection: 'column', overflow: 'hidden' }

  return (
    <div style={rootStyle}>
      <div style={{ padding: '12px 14px', borderBottom: '1px solid #0f3d1f', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 700, fontSize: 13, color: '#39ff6a' }}>Editar nodo</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          {onDuplicate && (
            <button onClick={() => onDuplicate(node.id)} style={{ background: 'none', border: 'none', color: '#00ff41', cursor: 'pointer', fontSize: 17, lineHeight: 1 }} title="Duplicar nodo">⧉</button>
          )}
          <button onClick={() => onDelete(node.id)} style={{ background: 'none', border: 'none', color: '#ff4444', cursor: 'pointer', fontSize: 18, lineHeight: 1 }} title="Eliminar nodo">🗑</button>
          {mobile && onClose && (
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#3d7a4a', cursor: 'pointer', fontSize: 20, lineHeight: 1 }} aria-label="Cerrar">✕</button>
          )}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '14px', display: 'flex', flexDirection: 'column', gap: 14 }}>

        {tipo === 'inicio' && <>
          <div>
            <label style={{ color: '#3d7a4a', fontSize: 11, display: 'block', marginBottom: 4, fontWeight: 600 }}>PALABRAS CLAVE</label>
            <input value={kwText}
              onChange={e => setKwText(e.target.value)}
              onBlur={() => setCfg(c => ({ ...c, palabrasClave: parsearPalabrasClave(kwText) }))}
              placeholder="qr, pago, precio, hola" style={inputStyle} />
            <p style={{ margin: '4px 0 0', color: '#3d6b45', fontSize: 10 }}>Separadas por comas</p>
          </div>
          <div>
            <label style={{ color: '#3d7a4a', fontSize: 11, display: 'block', marginBottom: 8, fontWeight: 600 }}>MODO DE COINCIDENCIA</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {['contiene', 'exacto'].map(m => (
                <button key={m} onClick={() => setCfg(c => ({ ...c, modoCoincidencia: m }))}
                  style={{ flex: 1, padding: '7px 0', borderRadius: 7, border: `1.5px solid ${cfg.modoCoincidencia === m || (!cfg.modoCoincidencia && m === 'contiene') ? '#00ff41' : '#1a5c2e'}`, background: cfg.modoCoincidencia === m || (!cfg.modoCoincidencia && m === 'contiene') ? '#003d0f' : 'transparent', color: cfg.modoCoincidencia === m || (!cfg.modoCoincidencia && m === 'contiene') ? '#00ff41' : '#3d7a4a', cursor: 'pointer', fontSize: 12, fontWeight: 600, textTransform: 'capitalize' }}>
                  {m}
                </button>
              ))}
            </div>
            <p style={{ margin: '6px 0 0', color: '#3d6b45', fontSize: 10 }}>
              {(cfg.modoCoincidencia || 'contiene') === 'contiene'
                ? '"hola amigo" también dispara si la clave es "hola"'
                : 'El mensaje debe ser exactamente la palabra clave'}
            </p>
          </div>
        </>}

        {tipo === 'texto' && (
          <div>
            <label style={{ color: '#3d7a4a', fontSize: 11, display: 'block', marginBottom: 4, fontWeight: 600 }}>MENSAJE</label>
            <textarea value={cfg.texto || ''} onChange={e => setCfg(c => ({ ...c, texto: e.target.value }))}
              rows={7} placeholder="Escribe el mensaje que se enviará…"
              style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }} />
          </div>
        )}

        {['imagen', 'documento', 'audio', 'video'].includes(tipo) && <>
          <div>
            <label style={{ color: '#3d7a4a', fontSize: 11, display: 'block', marginBottom: 6, fontWeight: 600 }}>ARCHIVO</label>
            {cfg.mediaUrl && (
              <div style={{ background: '#000f03', borderRadius: 7, padding: '8px 10px', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 18 }}>{tipo === 'imagen' ? '🖼' : tipo === 'audio' ? '🎵' : tipo === 'video' ? '🎬' : '📄'}</span>
                <span style={{ color: '#66ffb3', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{cfg.nombre || 'archivo'}</span>
              </div>
            )}
            {tipo === 'imagen' && cfg.mediaUrl && (() => {
              const backendUrl = process.env.REACT_APP_BACKEND_URL || ''
              const src = cfg.mediaUrl.startsWith('http') ? cfg.mediaUrl : `${backendUrl}${cfg.mediaUrl}`
              return <img src={src} alt="preview" style={{ width: '100%', borderRadius: 6, marginBottom: 8, maxHeight: 140, objectFit: 'cover' }} />
            })()}
            {tipo === 'video' && cfg.mediaUrl && (() => {
              const backendUrl = process.env.REACT_APP_BACKEND_URL || ''
              const src = cfg.mediaUrl.startsWith('http') ? cfg.mediaUrl : `${backendUrl}${cfg.mediaUrl}`
              return <video src={src} controls style={{ width: '100%', borderRadius: 6, marginBottom: 8, maxHeight: 140 }} />
            })()}
            <button onClick={() => fileRef.current?.click()} disabled={subiendo}
              style={{ width: '100%', padding: '9px 0', borderRadius: 7, border: '1.5px dashed #1a5c2e', background: 'transparent', color: '#3d7a4a', cursor: 'pointer', fontSize: 12 }}>
              {subiendo ? 'Subiendo…' : cfg.mediaUrl ? '↑ Cambiar archivo' : '+ Subir archivo'}
            </button>
            <input ref={fileRef} type="file" accept={acceptFor[tipo] || '*'} style={{ display: 'none' }}
              onChange={e => { if (e.target.files[0]) subirMedia(e.target.files[0]) }} />
          </div>
          {tipo !== 'audio' && (
            <div>
              <label style={{ color: '#3d7a4a', fontSize: 11, display: 'block', marginBottom: 4, fontWeight: 600 }}>CAPTION (opcional)</label>
              <input value={cfg.caption || ''} onChange={e => setCfg(c => ({ ...c, caption: e.target.value }))}
                placeholder="Texto que acompaña al archivo" style={inputStyle} />
            </div>
          )}
        </>}

        {tipo === 'esperar' && (() => {
          // Compatibilidad con flujos viejos guardados como { segundos: N }
          const unidad = cfg.unidad || 'segundos'
          const cantidad = cfg.unidad ? cfg.cantidad : (cfg.segundos ?? 3)
          const limite = LIMITES_UNIDAD[unidad] || 59
          return (
            <div>
              <label style={{ color: '#3d7a4a', fontSize: 11, display: 'block', marginBottom: 4, fontWeight: 600 }}>TIEMPO DE ESPERA</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input type="number" min={1} max={limite} value={cantidad ?? ''}
                  onChange={e => {
                    const raw = e.target.value
                    // Permite borrar y escribir libremente; el clamp final ocurre en onBlur
                    if (raw === '') { setCfg(c => ({ ...c, unidad, cantidad: '' })); return }
                    const n = parseInt(raw, 10)
                    if (!Number.isNaN(n)) setCfg(c => ({ ...c, unidad, cantidad: n }))
                  }}
                  onBlur={() => setCfg(c => ({ ...c, unidad, cantidad: clampCantidad(unidad, cantidad) }))}
                  className="no-spinner"
                  style={{ ...inputStyle, width: 80, flexShrink: 0 }} />
                <select value={unidad}
                  onChange={e => {
                    const nuevaUnidad = e.target.value
                    setCfg(c => ({ ...c, unidad: nuevaUnidad, cantidad: clampCantidad(nuevaUnidad, cantidad) }))
                  }}
                  style={{ ...inputStyle, flex: 1 }}>
                  <option value="segundos">Segundos</option>
                  <option value="minutos">Minutos</option>
                  <option value="horas">Horas</option>
                  <option value="dias">Días</option>
                </select>
              </div>
              <p style={{ margin: '4px 0 0', color: '#3d6b45', fontSize: 10 }}>Entre 1 y {limite} {unidad}</p>
              {unidad !== 'segundos' && (
                <p style={{ margin: '6px 0 0', color: '#5fae6f', fontSize: 10 }}>
                  💡 Ideal para seguimiento: el cliente recibirá el siguiente mensaje aunque el servidor se reinicie mientras tanto.
                </p>
              )}
            </div>
          )
        })()}
      </div>

      <div style={{ padding: '12px 14px', borderTop: '1px solid #0f3d1f' }}>
        <button onClick={save} style={{ width: '100%', padding: '10px 0', borderRadius: 8, border: 'none', background: '#00ff41', color: '#000', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
          Aplicar cambios
        </button>
      </div>
    </div>
  )
}

// ── Editor principal ───────────────────────────────────────────────────────────
function FlujosEditorInner() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [flujo, setFlujo] = useState(null)
  const [nombre, setNombre] = useState('')
  const [activo, setActivo] = useState(true)
  const [nodes, setNodes, onNodesChange] = useNodesState([])
  const [edges, setEdges, onEdgesChange] = useEdgesState([])
  const [selectedNode, setSelectedNode] = useState(null)
  const [guardando, setGuardando] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [paletaOpen, setPaletaOpen] = useState(false)
  const { screenToFlowPosition } = useReactFlow()
  const canvasWrapperRef = useRef(null)

  // Detectar mobile y escuchar resize
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  // Ocultar el bottom-nav de la app mientras se edita el flujo (el editor es
  // una herramienta a pantalla completa). Ver regla CSS body.flow-editor-open.
  useEffect(() => {
    document.body.classList.add('flow-editor-open')
    return () => document.body.classList.remove('flow-editor-open')
  }, [])

  useEffect(() => {
    api.get(`/bot/flujos/${id}`).then(r => {
      const f = r.data
      setFlujo(f)
      setNombre(f.nombre)
      setActivo(f.activo !== false)
      const { rfNodes, rfEdges } = dbToFlow(f.nodos, f.conexiones)
      setNodes(rfNodes)
      setEdges(rfEdges)
    }).catch(() => toast.error('Error cargando flujo'))
  }, [id])

  const onConnect = useCallback(params => {
    setEdges(eds => addEdge({
      ...params,
      type: 'eliminable',
      markerEnd: { type: MarkerType.ArrowClosed, color: '#00ff41' },
      style: { stroke: '#00ff41', strokeWidth: 2 },
    }, eds))
  }, [])

  const onNodeClick = useCallback((_, node) => setSelectedNode(node), [])
  const onPaneClick = useCallback(() => setSelectedNode(null), [])

  const agregarNodo = (tipo, position) => {
    const defConfig = tipo === 'inicio' ? { palabrasClave: [], modoCoincidencia: 'contiene' }
      : tipo === 'esperar' ? { cantidad: 3, unidad: 'segundos' }
      : tipo === 'texto' ? { texto: '' } : {}
    // Sin posición explícita (clic normal, no arrastrado): centrar el nodo
    // nuevo en lo que se ve ahora mismo del canvas, no en un punto fijo que
    // puede quedar fuera de vista si el usuario hizo zoom/pan.
    let pos = position
    if (!pos) {
      const rect = canvasWrapperRef.current?.getBoundingClientRect()
      const jitter = () => (Math.random() - 0.5) * 80
      pos = rect
        ? screenToFlowPosition({ x: rect.left + rect.width / 2 + jitter(), y: rect.top + rect.height / 2 + jitter() })
        : { x: 160 + Math.random() * 200, y: 120 + Math.random() * 160 }
    }
    const nodo = { id: genId(), type: tipo, position: pos, data: { config: defConfig } }
    setNodes(ns => [...ns, nodo])
    if (isMobile) setPaletaOpen(false)
  }

  // Arrastrar y soltar desde la paleta al canvas
  const onDragStartPaleta = (e, tipo) => {
    e.dataTransfer.setData('application/reactflow', tipo)
    e.dataTransfer.effectAllowed = 'move'
  }
  const onDragOverCanvas = useCallback(e => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }, [])
  const onDropCanvas = e => {
    e.preventDefault()
    const tipo = e.dataTransfer.getData('application/reactflow')
    if (!tipo) return
    const position = screenToFlowPosition({ x: e.clientX, y: e.clientY })
    agregarNodo(tipo, position)
  }

  const actualizarNodo = (nodeId, cfg) => {
    setNodes(ns => ns.map(n => n.id === nodeId ? { ...n, data: { ...n.data, config: cfg } } : n))
    setSelectedNode(prev => prev?.id === nodeId ? { ...prev, data: { ...prev.data, config: cfg } } : prev)
  }

  const eliminarNodo = (nodeId) => {
    setNodes(ns => ns.filter(n => n.id !== nodeId))
    setEdges(es => es.filter(e => e.source !== nodeId && e.target !== nodeId))
    setSelectedNode(null)
  }

  const duplicarNodo = (nodeId) => {
    const original = nodes.find(n => n.id === nodeId)
    if (!original) return
    const copia = {
      id: genId(), type: original.type,
      position: { x: original.position.x + 40, y: original.position.y + 40 },
      data: { config: JSON.parse(JSON.stringify(original.data.config || {})) },
    }
    setNodes(ns => [...ns, copia])
    setSelectedNode(copia) // las conexiones no se copian: el nuevo nodo queda suelto para reconectar donde se quiera
    toast.success('Nodo duplicado')
  }

  // Reacomoda los nodos en columnas verticales siguiendo las conexiones desde
  // cada raíz (nodo sin entradas). Los nodos sueltos (sin conexión) quedan al final.
  const ordenarNodos = () => {
    if (!nodes.length) return
    const childrenOf = new Map()
    edges.forEach(e => {
      if (!childrenOf.has(e.source)) childrenOf.set(e.source, [])
      childrenOf.get(e.source).push(e.target)
    })
    const conEntrada = new Set(edges.map(e => e.target))
    const raices = nodes.filter(n => !conEntrada.has(n.id)).map(n => n.id)
    const rootIds = raices.length ? raices : [nodes[0].id]

    const VSPACE = 160, HSPACE = 280, START_X = 160, START_Y = 80
    const visitados = new Set()
    const posiciones = new Map()
    let columna = 0

    const colocar = (id, nivel) => {
      if (visitados.has(id)) return
      visitados.add(id)
      posiciones.set(id, { x: START_X + columna * HSPACE, y: START_Y + nivel * VSPACE })
      const hijos = childrenOf.get(id) || []
      if (hijos.length === 0) { columna++; return }
      hijos.forEach((hijoId, i) => {
        if (i > 0) columna++
        colocar(hijoId, nivel + 1)
      })
    }
    rootIds.forEach(id => { colocar(id, 0); columna++ })

    // Nodos sueltos que ninguna raíz alcanzó (ej. tras desconectar una línea)
    nodes.forEach(n => {
      if (!visitados.has(n.id)) {
        posiciones.set(n.id, { x: START_X + columna * HSPACE, y: START_Y })
        columna++
      }
    })

    setNodes(ns => ns.map(n => ({ ...n, position: posiciones.get(n.id) || n.position })))
    toast.success('Nodos ordenados')
  }

  const guardar = async () => {
    setGuardando(true)
    try {
      const { nodos, conexiones } = flowToDb(nodes, edges)
      const inicioNode = nodos.find(n => n.tipo === 'inicio')
      await api.put(`/bot/flujos/${id}`, {
        nombre, activo, nodos, conexiones,
        palabrasClave: inicioNode?.config?.palabrasClave || [],
        trigger: 'palabra_clave',
      })
      toast.success('Flujo guardado')
    } catch { toast.error('Error guardando') }
    finally { setGuardando(false) }
  }

  if (!flujo) return (
    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000000', color: '#3d7a4a', fontFamily: MONO }}>
      Cargando editor…
    </div>
  )

  // ── Piezas reutilizables ──
  const paletaBotones = PALETA.map(p => (
    <button key={p.tipo} onClick={() => agregarNodo(p.tipo)}
      draggable={!isMobile}
      onDragStart={e => onDragStartPaleta(e, p.tipo)}
      style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: isMobile ? '11px 12px' : '8px 10px', marginBottom: 6, borderRadius: 8, border: '1.5px solid #0f3d1f', background: isMobile ? '#000f03' : 'transparent', color: '#5fae6f', cursor: isMobile ? 'pointer' : 'grab', fontSize: 12, textAlign: 'left', transition: 'border-color 0.15s' }}
      onMouseEnter={e => e.currentTarget.style.borderColor = p.color}
      onMouseLeave={e => e.currentTarget.style.borderColor = '#0f3d1f'}>
      <span style={{ fontSize: 18 }}>{p.icon}</span>
      <div>
        <div style={{ fontWeight: 600, lineHeight: 1.3, color: '#c8ffd4' }}>{p.label}</div>
        <div style={{ fontSize: 10, color: '#3d6b45', lineHeight: 1.2 }}>{p.desc}</div>
      </div>
    </button>
  ))

  const canvas = (
    <>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={NODE_TYPES}
        edgeTypes={EDGE_TYPES}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        fitView
        deleteKeyCode="Delete"
        minZoom={0.2}
        style={{ background: '#000d02' }}>
        <Controls style={{ background: '#0f3d1f', border: '1px solid #1f4d2a', borderRadius: 8 }} showInteractive={!isMobile} />
        <Background color="#0f3d1f" gap={24} size={1} />
      </ReactFlow>

      {nodes.length === 0 && (
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', color: '#3d7a4a', pointerEvents: 'none', userSelect: 'none', width: '85%' }}>
          <div style={{ fontSize: 52, marginBottom: 12 }}>🔗</div>
          <p style={{ fontSize: 14, margin: 0 }}>{isMobile ? 'Toca "➕ Nodo" para empezar' : 'Agrega nodos desde el panel izquierdo'}</p>
          <p style={{ fontSize: 12, margin: '4px 0 0' }}>Empieza con un nodo Inicio y conecta los mensajes</p>
        </div>
      )}
    </>
  )

  // ══════════════ MOBILE ══════════════
  if (isMobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#000000', color: '#39ff6a', fontFamily: MONO, overflow: 'hidden' }}>

        {/* Barra superior compacta */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderBottom: '1px solid #0f3d1f', background: '#020f04', flexShrink: 0 }}>
          <button onClick={() => navigate('/sesiones')}
            style={{ background: 'none', border: 'none', color: '#5fae6f', cursor: 'pointer', fontSize: 22, padding: '2px 6px 2px 0', flexShrink: 0 }} aria-label="Volver">←</button>
          <input value={nombre} onChange={e => setNombre(e.target.value)}
            placeholder="Nombre del flujo" style={{ ...inputStyle, flex: 1, minWidth: 0 }} />
          <button onClick={ordenarNodos} title="Ordenar nodos"
            style={{ flexShrink: 0, padding: '9px 10px', borderRadius: 8, border: '1.5px solid #1a5c2e', background: 'transparent', color: '#5fae6f', cursor: 'pointer', fontSize: 15 }}>
            🧹
          </button>
          <button onClick={guardar} disabled={guardando}
            style={{ flexShrink: 0, padding: '9px 14px', borderRadius: 8, border: 'none', background: guardando ? '#1f4d2a' : '#00ff41', color: '#000', cursor: guardando ? 'default' : 'pointer', fontWeight: 700, fontSize: 13 }}>
            {guardando ? '⏳' : '💾'}
          </button>
        </div>

        {/* Fila secundaria: activo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderBottom: '1px solid #0f3d1f', background: '#001a06', flexShrink: 0 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={activo} onChange={e => setActivo(e.target.checked)} />
            <span style={{ color: '#5fae6f', fontSize: 12 }}>Flujo activo</span>
          </label>
        </div>

        {/* Canvas full-width */}
        <div ref={canvasWrapperRef} style={{ flex: 1, position: 'relative', minHeight: 0 }}
          onDragOver={onDragOverCanvas} onDrop={onDropCanvas}>
          {canvas}

          {/* Botón flotante para agregar nodo */}
          <button onClick={() => setPaletaOpen(true)}
            style={{ position: 'absolute', left: 16, bottom: 16, zIndex: 5, padding: '12px 18px', borderRadius: 999, border: 'none', background: '#00ff41', color: '#000', fontWeight: 700, fontSize: 14, boxShadow: '0 4px 16px rgba(0,255,65,0.55)', cursor: 'pointer' }}>
            ➕ Nodo
          </button>
        </div>

        {/* Hoja: paleta de nodos */}
        {paletaOpen && (
          <>
            <div onClick={() => setPaletaOpen(false)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 40 }} />
            <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 41, background: '#020f04', borderRadius: '16px 16px 0 0', borderTop: '1px solid #0f3d1f', maxHeight: '75vh', overflowY: 'auto', padding: '8px 14px 24px' }}>
              <div style={{ width: 40, height: 4, borderRadius: 2, background: '#1f4d2a', margin: '8px auto 14px' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ fontWeight: 700, fontSize: 14, color: '#39ff6a' }}>Agregar nodo</span>
                <button onClick={() => setPaletaOpen(false)} style={{ background: 'none', border: 'none', color: '#3d7a4a', cursor: 'pointer', fontSize: 20 }} aria-label="Cerrar">✕</button>
              </div>
              {paletaBotones}
            </div>
          </>
        )}

        {/* Hoja: editar nodo */}
        {selectedNode && (
          <>
            <div onClick={() => setSelectedNode(null)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 40 }} />
            <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 41, borderRadius: '16px 16px 0 0', overflow: 'hidden', borderTop: '1px solid #0f3d1f' }}>
              <PanelProps
                key={selectedNode.id}
                node={selectedNode}
                flujoId={id}
                onChange={actualizarNodo}
                onDelete={eliminarNodo}
                onDuplicate={duplicarNodo}
                mobile
                onClose={() => setSelectedNode(null)}
              />
            </div>
          </>
        )}
      </div>
    )
  }

  // ══════════════ DESKTOP ══════════════
  return (
    <div style={{ display: 'flex', height: '100%', background: '#000000', color: '#39ff6a', fontFamily: MONO, overflow: 'hidden' }}>

      {/* ── Sidebar izquierdo ── */}
      <div style={{ width: 215, flexShrink: 0, borderRight: '1px solid #0f3d1f', background: '#020f04', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '12px 12px 10px', borderBottom: '1px solid #0f3d1f' }}>
          <button onClick={() => navigate('/sesiones')}
            style={{ background: 'none', border: 'none', color: '#3d7a4a', cursor: 'pointer', fontSize: 12, padding: 0, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 4 }}>
            ← Volver
          </button>
          <input value={nombre} onChange={e => setNombre(e.target.value)}
            placeholder="Nombre del flujo" style={{ ...inputStyle, marginBottom: 8 }} />
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={activo} onChange={e => setActivo(e.target.checked)} />
            <span style={{ color: '#5fae6f', fontSize: 12 }}>Flujo activo</span>
          </label>
        </div>

        <div style={{ padding: '10px 12px', flex: 1, overflowY: 'auto' }}>
          <p style={{ color: '#3d6b45', fontSize: 10, letterSpacing: 1, marginBottom: 8, fontWeight: 700 }}>AGREGAR NODO</p>
          {paletaBotones}
        </div>

        <div style={{ padding: '10px 12px', borderTop: '1px solid #0f3d1f' }}>
          <p style={{ color: '#3d6b45', fontSize: 10, marginBottom: 6 }}>
            Conecta nodos arrastrando desde el punto inferior al superior del siguiente nodo. Toca el "×" sobre una conexión para desvincularla sin borrar los nodos.
          </p>
          <button onClick={ordenarNodos}
            style={{ width: '100%', padding: '9px 0', borderRadius: 8, border: '1.5px solid #1a5c2e', background: 'transparent', color: '#5fae6f', cursor: 'pointer', fontWeight: 600, fontSize: 12, marginBottom: 8 }}>
            🧹 Ordenar nodos
          </button>
          <button onClick={guardar} disabled={guardando}
            style={{ width: '100%', padding: '10px 0', borderRadius: 8, border: 'none', background: guardando ? '#1f4d2a' : '#00ff41', color: '#000', cursor: guardando ? 'default' : 'pointer', fontWeight: 700, fontSize: 13 }}>
            {guardando ? 'Guardando…' : '💾 Guardar flujo'}
          </button>
        </div>
      </div>

      {/* ── Canvas ReactFlow ── */}
      <div ref={canvasWrapperRef} style={{ flex: 1, position: 'relative' }}
        onDragOver={onDragOverCanvas} onDrop={onDropCanvas}>
        {canvas}
      </div>

      {/* ── Panel de propiedades ── */}
      {selectedNode && (
        <PanelProps
          key={selectedNode.id}
          node={selectedNode}
          flujoId={id}
          onChange={actualizarNodo}
          onDelete={eliminarNodo}
          onDuplicate={duplicarNodo}
        />
      )}
    </div>
  )
}

export default function FlujosEditor() {
  return (
    <ReactFlowProvider>
      <FlujosEditorInner />
    </ReactFlowProvider>
  )
}
